import React from 'react';
import { FormikProps } from 'formik';
import type { Contacts, Context } from '../../shared/types';
import { Flex, TextV2, ButtonV2 } from '@withjoy/joykit';
import { Wrapper, Details, Footer } from '../components';
import { useConfirmationController } from './Confirmation.controller';
import { outlinedButtonOverrides } from '@apps/admin/saveTheDate/components/Shared.styles';
import { Profile, Phone, Email, Shipping } from '@withjoy/joykit/icons';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';

interface ConfirmationProps
  extends Readonly<{
    formik: FormikProps<Contacts>;
    context?: Context;
    isMutationLoading: boolean;
    hasMutationError: boolean;
  }> {}

export const Confirmation: React.FC<ConfirmationProps> = ({ formik, context, isMutationLoading, hasMutationError }) => {
  const { tAdmin, handleOnModifyDetailsClick, handleOnLooksGoodClick } = useConfirmationController(formik);
  const headOfHousehold = formik.values.contacts[0];

  if (isMutationLoading) return <JoyLogoLoader loaderKey="contact-collector-address-confirm" />;

  return (
    <Wrapper>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX="24px"
        width="100%"
      >
        <TextV2
          typographyVariant="hed6"
          marginTop={['24px', null, '4px']}
          textAlign="center"
        >
          {tAdmin.looksRight()}
        </TextV2>
        {context?.coupleNames && <Details coupleNames={context.coupleNames} />}
        <TextV2
          typographyVariant="hed4"
          marginTop="24px"
          marginBottom="12px"
          width="100%"
        >
          {tAdmin.contactInfo()}
        </TextV2>
        <Flex
          rowGap="15px"
          flexDirection="column"
          width="100%"
        >
          {formik.values.contacts.map((contact, index) => (
            <Flex
              key={index}
              rowGap="8px"
              flexDirection="column"
            >
              <Flex
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
              >
                <Profile color="mono12" />
                <TextV2
                  color="mono12"
                  typographyVariant="body2"
                  marginLeft="8px"
                >{`${contact.firstName} ${contact.lastName}`}</TextV2>
              </Flex>
              {contact.phoneNumber && (
                <Flex
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                >
                  <Phone color="mono12" />
                  <TextV2
                    color="mono12"
                    typographyVariant="body2"
                    marginLeft="8px"
                  >
                    {contact.phoneNumber}
                  </TextV2>
                </Flex>
              )}
              {contact.email && (
                <Flex
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                >
                  <Email color="mono12" />
                  <TextV2
                    color="mono12"
                    typographyVariant="body2"
                    marginLeft="8px"
                  >
                    {contact.email}
                  </TextV2>
                </Flex>
              )}
            </Flex>
          ))}
        </Flex>
        <TextV2
          typographyVariant="hed4"
          marginTop="24px"
          marginBottom="12px"
          width="100%"
        >
          {tAdmin.mailingAddress()}
        </TextV2>
        <Flex
          marginBottom="8px"
          width="100%"
        >
          <Shipping color="mono12" />
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            marginLeft="8px"
          >
            <TextV2
              color="mono12"
              typographyVariant="body2"
            >{`${headOfHousehold.address1}${headOfHousehold.address2 ? ` ${headOfHousehold.address2}` : ''}`}</TextV2>
            <TextV2
              color="mono12"
              typographyVariant="body2"
            >{`${headOfHousehold.city},  ${headOfHousehold.region} ${headOfHousehold.postalCode}`}</TextV2>
            <TextV2
              color="mono12"
              typographyVariant="body2"
            >
              {headOfHousehold.country}
            </TextV2>
          </Flex>
        </Flex>
        <ButtonV2
          overrides={outlinedButtonOverrides}
          variant="outline"
          marginTop="32px"
          onClick={handleOnModifyDetailsClick}
          fullWidth
        >
          {tAdmin.modifyDetails()}
        </ButtonV2>
        <ButtonV2
          marginTop="16px"
          onClick={handleOnLooksGoodClick}
          fullWidth
        >
          {tAdmin.looksGood()}
        </ButtonV2>
        {hasMutationError && (
          <TextV2
            typographyVariant="body3"
            color="red"
            marginTop="16px"
          >
            {tAdmin.contactError()}
          </TextV2>
        )}
        <Footer />
      </Flex>
    </Wrapper>
  );
};
